import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { BrandLogo } from "../components/UpiApps";

// p2m / p2p pay page — same hosted-checkout UI as p2g (/pay/{code}): it reuses the
// checkout stylesheet + markup served by the API from backend/static/pay.
const PAY_CSS = "/pay/assets/pay.css";
const REDIRECT_MS = 3000;

const POLL_MS = 2500;
const QUEUE_RETRY_MS = 4000;
const QUEUE_MAX_MS = 180000;
const ssKey = (code) => `sc_pay_${code}`;
// Closed links (expired / already used / disabled) render the server's Link Expired page.
const linkExpired = (code) => window.location.replace(`/pay/${encodeURIComponent(code)}/expired`);

function inr2(n) {
  if (n == null) return "₹ —";
  return `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
// Split an amount into "₹1,234" + ".56" (the checkout renders the paise smaller).
function amountParts(n) {
  const v = Number(n || 0);
  const rupees = "₹" + Math.floor(v).toLocaleString("en-IN");
  const paise = "." + String(Math.round((v - Math.floor(v)) * 100)).padStart(2, "0");
  return [rupees, paise];
}
function mmss(secs) {
  if (secs == null) return "—:—";
  const s = Math.max(0, secs);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

// ---- deep-link launching (normal browsers AND Android webviews) ----
const UA = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
const IS_ANDROID = /Android/i.test(UA);
function toAndroidIntent(url, fallbackUrl) {
  const at = url.indexOf("://");
  if (at < 0) return url;
  const scheme = url.slice(0, at);
  const rest = url.slice(at + 3);
  return `intent://${rest}#Intent;scheme=${scheme};S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`;
}
function launchDeepLink(url) {
  if (!url) return;
  const target = IS_ANDROID ? toAndroidIntent(url, window.location.href) : url;
  try {
    const a = document.createElement("a");
    a.href = target; a.style.display = "none";
    document.body.appendChild(a); a.click();
    setTimeout(() => { try { document.body.removeChild(a); } catch { /* ignore */ } }, 0);
  } catch {
    try { window.location.href = target; } catch { /* ignore */ }
  }
}
async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; }
  catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy");
      document.body.removeChild(ta); return true;
    } catch { return false; }
  }
}

export default function PayPage() {
  const { code } = useParams();
  const [link, setLink] = useState(null);
  const [err, setErr] = useState("");
  const [amountInput, setAmountInput] = useState(""); // open-amount links
  const [intent, setIntent] = useState(null);
  // loading | amount | choose | confirm | done | queued | expired | paid | disabled | error
  const [screen, setScreen] = useState("loading");
  const [remaining, setRemaining] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null); // "phonepe" | "paytm" | "upi"
  const [qrOpen, setQrOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [queued, setQueued] = useState(0);
  const [queueGaveUp, setQueueGaveUp] = useState(false);
  const [returnUrl, setReturnUrl] = useState(null); // signed success_url, auto-followed after 3 s
  const [redirectIn, setRedirectIn] = useState(null);
  const [utr, setUtr] = useState(null);

  const pollRef = useRef(null);
  const tickRef = useRef(null);
  const queueTimerRef = useRef(null);
  const queueTickRef = useRef(null);
  const queueStartRef = useRef(0);
  const linkRef = useRef(null);
  const busyRef = useRef(false); // in-flight intent create guard

  const stopTimers = useCallback(() => {
    [pollRef, tickRef, queueTimerRef, queueTickRef].forEach((r) => {
      if (r.current) { clearInterval(r.current); clearTimeout(r.current); r.current = null; }
    });
  }, []);
  useEffect(() => () => stopTimers(), [stopTimers]);

  const clearSaved = useCallback(() => {
    try { sessionStorage.removeItem(ssKey(code)); } catch { /* ignore */ }
  }, [code]);
  const saveSession = useCallback((data, app, scr) => {
    try { sessionStorage.setItem(ssKey(code), JSON.stringify({ ...data, selectedApp: app || null, screen: scr })); } catch { /* ignore */ }
  }, [code]);

  const goRedirect = (url) => {
    if (!url || !/^https?:\/\//i.test(url)) return;
    setReturnUrl(url);
    setRedirectIn(Math.round(REDIRECT_MS / 1000));
  };
  useEffect(() => {
    if (redirectIn == null || !returnUrl) return undefined;
    if (redirectIn <= 0) { window.location.replace(returnUrl); return undefined; }
    const t = setTimeout(() => setRedirectIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [redirectIn, returnUrl]);

  // Load the checkout stylesheet only while this page is mounted.
  useEffect(() => {
    const el = document.createElement("link");
    el.rel = "stylesheet"; el.href = PAY_CSS; el.dataset.payCss = "1";
    document.head.appendChild(el);
    document.body.classList.add("pay-checkout");
    return () => { el.remove(); document.body.classList.remove("pay-checkout"); };
  }, []);
  const withParams = (base, params) => {
    try {
      const u = new URL(base);
      Object.entries(params).forEach(([k, v]) => { if (v != null) u.searchParams.set(k, v); });
      return u.toString();
    } catch { return base; }
  };
  const goCancel = () => {
    const l = linkRef.current;
    if (!l?.cancel_url || !/^https?:\/\//i.test(l.cancel_url)) return;
    stopTimers(); clearSaved();
    window.location.href = withParams(l.cancel_url, { sc_status: "cancelled", order: code });
  };
  const goBackToMerchant = () => {
    const l = linkRef.current;
    if (!l?.back_url || !/^https?:\/\//i.test(l.back_url)) return;
    window.location.href = l.back_url;
  };

  const pollStatus = useCallback(async (intentId) => {
    try {
      const { data } = await api.get(`/intents/${intentId}/status`);
      if (data.settled) {
        stopTimers(); clearSaved(); setUtr(data.rrn || null); setScreen("done");
        if (data.redirect_url) goRedirect(data.redirect_url);
      } else if (data.status === "expired") {
        stopTimers(); clearSaved(); linkExpired(code);
      }
    } catch { /* transient — keep polling */ }
  }, [stopTimers, clearSaved, code]);

  const startTimers = useCallback((createdIntent) => {
    const expiresMs = new Date(createdIntent.expires_at).getTime();
    setRemaining(Math.max(0, Math.round((expiresMs - Date.now()) / 1000)));
    tickRef.current = setInterval(() => {
      const left = Math.max(0, Math.round((expiresMs - Date.now()) / 1000));
      setRemaining(left);
      if (left <= 0) pollStatus(createdIntent.intent_id);
    }, 1000);
    pollRef.current = setInterval(() => pollStatus(createdIntent.intent_id), POLL_MS);
    pollStatus(createdIntent.intent_id);
  }, [pollStatus]);

  // ---- create the payment intent (nudged amount); queue on pool-full ----
  const createIntent = useCallback((amountVal) => {
    if (busyRef.current) return;
    busyRef.current = true;
    const l = linkRef.current;
    const body = {};
    if (l?.amount_type === "open") body.amount = amountVal;

    const attempt = async () => {
      try {
        const { data } = await api.post(`/pay_id/${code}/intents`, body);
        if (queueTickRef.current) { clearInterval(queueTickRef.current); queueTickRef.current = null; }
        busyRef.current = false;
        setIntent(data);
        saveSession(data, null, "choose");
        setScreen("choose");
        startTimers(data);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const poolFull = e?.response?.status === 409 &&
          (detail?.reason === "pool_full" || (typeof detail === "string" && detail.toLowerCase().includes("too many")));
        const gone = e?.response?.status === 410;
        if (poolFull) {
          setScreen("queued");
          if (!queueTickRef.current) { setQueued(0); setQueueGaveUp(false); queueStartRef.current = Date.now(); queueTickRef.current = setInterval(() => setQueued((s) => s + 1), 1000); }
          if (Date.now() - queueStartRef.current > QUEUE_MAX_MS) {
            if (queueTickRef.current) { clearInterval(queueTickRef.current); queueTickRef.current = null; }
            setQueueGaveUp(true); busyRef.current = false; return;
          }
          queueTimerRef.current = setTimeout(attempt, QUEUE_RETRY_MS + Math.floor(Math.random() * 800));
        } else if (gone) {
          busyRef.current = false;
          const msg = (typeof detail === "string" ? detail : detail?.message || "").toLowerCase();
          if (msg.includes("paid") || msg.includes("expired")) { clearSaved(); linkExpired(code); return; }
          setScreen("disabled");
        } else {
          busyRef.current = false;
          setScreen(l?.amount_type === "open" ? "amount" : "error");
          if (l?.amount_type === "open") toast.error(formatApiError(detail) || "Couldn't start payment");
          else setErr(formatApiError(detail) || "Couldn't start payment");
        }
      }
    };
    attempt();
  }, [code, saveSession, startTimers]);

  // ---- load link + resume any in-flight payment (refresh-resilient) ----
  useEffect(() => {
    let alive = true;
    api.get(`/pay_id/${code}`)
      .then(({ data }) => {
        if (!alive) return;
        setLink(data); linkRef.current = data;
        if (data.payment_mode === "p2g") { window.location.replace(`/pay/${encodeURIComponent(code)}`); return; }
        // Expired / already-used / disabled link: same Link Expired page as p2g.
        if (["paid", "expired", "disabled"].includes(data.status)) { clearSaved(); linkExpired(code); return; }
        try {
          const saved = JSON.parse(sessionStorage.getItem(ssKey(code)) || "null");
          if (saved && saved.expires_at && new Date(saved.expires_at).getTime() > Date.now()) {
            setIntent(saved);
            if (saved.selectedApp) setSelectedApp(saved.selectedApp);
            setScreen(saved.screen === "confirm" ? "confirm" : "choose");
            startTimers(saved);
            return;
          }
        } catch { /* ignore */ }
        clearSaved();
        if (data.amount_type === "open") setScreen("amount");
        else createIntent();
      })
      .catch((e) => { if (alive) { setErr(e.response?.data?.detail || "Link not found"); setScreen("error"); } });
    return () => { alive = false; };
  }, [code, clearSaved, startTimers, createIntent]);

  // ---- actions ----
  const recordApp = (intentId, appKey) => { if (appKey !== "upi") api.post(`/intents/${intentId}/select-app`, { app: appKey }).catch(() => {}); };
  const pick = (appKey, url) => {
    setSelectedApp(appKey);
    setQrOpen(false);
    if (intent) { recordApp(intent.intent_id, appKey); saveSession(intent, appKey, "confirm"); }
    launchDeepLink(url);
    setScreen("confirm");
  };
  const useDifferentApp = () => {
    setSelectedApp(null);
    if (intent) saveSession(intent, null, "choose");
    setScreen("choose");
  };
  const doCopy = async () => {
    if (await copyText(intent?.vpa || "")) { setCopied(true); setTimeout(() => setCopied(false), 1600); }
    else toast.error("Couldn't copy");
  };
  const submitAmount = () => {
    const amt = parseInt(amountInput || "0", 10);
    if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
    createIntent(amt);
  };
  const cancelQueue = () => { stopTimers(); setQueued(0); setQueueGaveUp(false); busyRef.current = false; setScreen(linkRef.current?.amount_type === "open" ? "amount" : "error"); };
  const resumeQueue = () => {
    setQueueGaveUp(false); setQueued(0); queueStartRef.current = Date.now();
    if (!queueTickRef.current) queueTickRef.current = setInterval(() => setQueued((s) => s + 1), 1000);
    busyRef.current = false;
    createIntent(parseInt(amountInput || "0", 10) || undefined);
  };

  // ---- derived ----
  const apps = intent?.apps?.length ? intent.apps : []; // P2P deep links (PhonePe/Paytm); empty for P2M
  const displayAmount =
    intent != null ? Number(intent.amount)
      : link?.amount_type === "fixed" ? Number(link.amount)
      : amountInput ? parseInt(amountInput || "0", 10) : null;
  const [rupees, paise] = amountParts(displayAmount);
  const merchantName = intent?.merchant_name || link?.merchant_name || "Merchant";
  const customer = (link?.customer_name || "").trim();
  const greeting = customer ? `Hi, ${customer}` : "Hi there";
  const amountLen = rupees.length;
  const amountSize = amountLen > 20 ? "amount--long" : amountLen > 14 ? "amount--small" : amountLen > 10 ? "amount--medium" : "";
  const waiting = screen === "confirm";
  const appName = { phonepe: "PhonePe", paytm: "Paytm" }[selectedApp] || "your UPI app";

  const amountEl = (
    <p className={`amount ${amountSize}`} aria-label={`Amount ${inr2(displayAmount)}`}>
      <span>{rupees}</span><span className="amount-decimals">{paise}</span>
    </p>
  );

  return (
    <div className="checkout-wrap" data-testid="pay-page">
      <main className="checkout" id="pay" data-status={screen}>
        {/* PAY — choose / confirm share the SuryaPe pending ticket */}
        {(screen === "choose" || screen === "confirm") && (
          <section aria-label="Complete your payment">
            <article className="payment-ticket pending-ticket">
              <BrandEdge />
              <div className="utility-row">
                {intent?.qr_data_url ? (
                  <details className="qr-details" open={qrOpen} onToggle={(e) => setQrOpen(e.currentTarget.open)}>
                    <summary data-testid="pay-qr-toggle"><QrGlyph /><span>Show QR</span><Chevron /></summary>
                    <div className="qr-body">
                      <div className="qr" role="img" aria-label={`Scan this QR code with a UPI app to pay ${inr2(displayAmount)}`}>
                        <img src={intent.qr_data_url} alt="" data-testid="pay-qr" style={{ width: "100%", height: "100%", display: "block" }} />
                      </div>
                      <button type="button" className="cancel-payment" onClick={doCopy} data-testid="pay-copy-vpa" style={{ marginTop: 8 }}>{copied ? "UPI ID copied" : "Copy UPI ID"}</button>
                    </div>
                  </details>
                ) : <span />}
                <div className="payment-timer" role="timer" aria-label="Time remaining"><span data-testid="pay-countdown">{mmss(remaining)}</span></div>
              </div>

              <div className="payment-summary">
                <h1 className="greeting" title={greeting}>{greeting}</h1>
                <ResultIcons />
                {amountEl}
                <p className="merchant-name" title={`Paying ${merchantName}`}>Paying <span>{merchantName}</span></p>
              </div>

              {/* Reconciliation is on the exact amount — the paise must not change. */}
              <p className="status-text" data-testid="pay-exact-note" style={{ marginTop: 0, marginBottom: 12, color: "var(--ink-2)" }}>
                Pay exactly <strong style={{ color: "var(--ink)" }}>{inr2(displayAmount)}</strong> — don't change the paise.
              </p>

              {apps.length > 0 ? (
                // P2P: white app buttons side by side, each filled by the app's own logo (original design).
                <div style={{ display: "flex", gap: 10 }}>
                  {apps.map((a) => (
                    <a key={a.key} role="button" href={a.intent} data-testid={`pay-app-${a.key}`} className="pp-app"
                      onClick={(e) => { e.preventDefault(); pick(a.key, a.intent); }} aria-label={`Pay with ${a.label}`}
                      style={{ flex: "1 1 0", minWidth: 0, minHeight: 58, display: "flex", alignItems: "center", justifyContent: "center", padding: "10px", textDecoration: "none" }}>
                      <BrandLogo app={a.key} style={{ height: a.key === "paytm" ? 26 : 38, width: "auto", maxWidth: "88%" }} />
                    </a>
                  ))}
                </div>
              ) : (
                <a className="upi-button" role="button" href={intent?.upi_intent} data-testid="pay-generic"
                  onClick={(e) => { e.preventDefault(); pick("upi", intent?.upi_intent); }}>
                  <AppIcons />
                  <span className="upi-button-label">Pay via UPI App</span>
                  <Arrow />
                </a>
              )}

              {waiting ? (
                <>
                  <div className="payment-waiting" role="status" aria-live="polite" data-testid="pay-confirming">
                    <span className="waiting-spinner" aria-hidden="true" />
                    <div className="waiting-copy">
                      <p className="waiting-title">Waiting for payment status</p>
                      <p className="waiting-message">Finish in {appName}, then come back — we confirm it automatically.</p>
                    </div>
                  </div>
                  <button type="button" className="cancel-payment" onClick={useDifferentApp} data-testid="pay-different-app">Use a different app</button>
                </>
              ) : (
                <p className="status-text" role="status" aria-live="polite">Verifying payment automatically…</p>
              )}
              {link?.cancel_url && <button type="button" className="cancel-payment" onClick={goCancel} data-testid="pay-cancel">Cancel payment</button>}
              <footer className="checkout-footer">For help with this payment, contact the merchant.</footer>
            </article>
          </section>
        )}

        {/* OPEN-AMOUNT ENTRY */}
        {screen === "amount" && (
          <section aria-label="Enter amount">
            <article className="payment-ticket pending-ticket">
              <BrandEdge />
              <div className="payment-summary">
                <h1 className="greeting">{greeting}</h1>
                <p className="merchant-name">Paying <span>{merchantName}</span></p>
                <label className="amount" style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 2 }}>
                  <span>₹</span>
                  <input type="number" inputMode="numeric" min="1" value={amountInput} autoFocus data-testid="pay-amount-input"
                    onChange={(e) => setAmountInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitAmount()}
                    placeholder="0" aria-label="Amount in rupees"
                    style={{ width: "5.5ch", border: "none", outline: "none", background: "transparent", font: "inherit", color: "inherit", textAlign: "left" }} />
                </label>
              </div>
              <button type="button" className="upi-button" onClick={submitAmount} data-testid="pay-amount-continue" style={{ width: "100%", border: 0 }}>
                <span className="upi-button-label">Continue</span><Arrow />
              </button>
              <footer className="checkout-footer">For help with this payment, contact the merchant.</footer>
            </article>
          </section>
        )}

        {/* QUEUED (pool full) */}
        {screen === "queued" && (
          <section aria-label="Waiting for a payment slot">
            <article className="payment-ticket pending-ticket" data-testid="pay-queued">
              <BrandEdge />
              <div className="payment-summary">
                <h1 className="greeting">{queueGaveUp ? "Still busy" : "You're in line"}</h1>
                {amountEl}
                <p className="merchant-name">Paying <span>{merchantName}</span></p>
              </div>
              <div className="payment-waiting" role="status" aria-live="polite">
                <span className="waiting-spinner" aria-hidden="true" />
                <div className="waiting-copy">
                  <p className="waiting-title">{queueGaveUp ? "It's been busy for a bit" : "Holding your spot"} · {mmss(queued)}</p>
                  <p className="waiting-message">{queueGaveUp ? "Keep waiting, or come back shortly." : "We'll start your payment the moment a spot opens."}</p>
                </div>
              </div>
              {queueGaveUp && <button type="button" className="upi-button" onClick={resumeQueue} style={{ width: "100%", border: 0, marginBottom: 6 }}><span className="upi-button-label">Keep waiting</span></button>}
              <button type="button" className="cancel-payment" onClick={cancelQueue}>Cancel</button>
            </article>
          </section>
        )}

        {/* LOADING */}
        {screen === "loading" && (
          <section aria-label="Loading">
            <article className="payment-ticket pending-ticket">
              <BrandEdge />
              <div className="payment-waiting" role="status" style={{ marginTop: 8 }}>
                <span className="waiting-spinner" aria-hidden="true" />
                <div className="waiting-copy"><p className="waiting-title">Preparing your payment…</p></div>
              </div>
            </article>
          </section>
        )}

        {/* SUCCESS — SuryaPe receipt */}
        {screen === "done" && (
          <section className="panel success" aria-labelledby="done-title" data-testid="pay-success">
            <article className="payment-ticket receipt-paper">
              <BrandEdge />
              <header className="receipt-heading">
                <ResultIcons />
                <h1 id="done-title">Payment Success!</h1>
                <p className="done-message">Your payment has been successfully confirmed.</p>
              </header>
              <div className="receipt-rule" aria-hidden="true" />
              <p className="amount-label">Total Payment</p>
              {amountEl}
              {utr && (
                <div className="receipt-utr"><span className="receipt-utr-label">UTR</span><span className="receipt-utr-value" data-testid="pay-utr">{utr}</span></div>
              )}
              <footer className="receipt-footer">
                <p className="receipt-note receipt-note--success">Paid to {merchantName} · Ref {code}</p>
              </footer>
            </article>
            {returnUrl ? (
              <>
                <p className="redirect-message">Returning to merchant in <span>{Math.max(0, redirectIn ?? 0)}</span> seconds…</p>
                <a className="return-link" href={returnUrl}>Return now</a>
              </>
            ) : link?.back_url ? (
              <button type="button" className="return-link" onClick={goBackToMerchant} data-testid="pay-back" style={{ background: "none", border: 0 }}>Return to merchant</button>
            ) : null}
          </section>
        )}

        {/* NOT FOUND / NOT ACCEPTING — same receipt card as success / failed / Link Expired */}
        {["disabled", "error"].includes(screen) && (
          <section className="panel expired" aria-labelledby="done-title" data-testid={`pay-${screen}`}>
            <article className="payment-ticket receipt-paper">
              <BrandEdge />
              <header className="receipt-heading">
                <ResultIcons />
                <h1 id="done-title">{screen === "disabled" ? "Payment Unavailable" : "Payment Link Not Found"}</h1>
                <p className="done-message">{screen === "disabled" ? "This merchant is not accepting payments right now." : (err && typeof err === "string" ? err : "Ask the merchant for a new link to continue.")}</p>
              </header>
              <footer className="receipt-footer"><p className="receipt-document-note">For payment or order queries, contact the merchant.</p></footer>
            </article>
            {link?.back_url && <button type="button" className="return-link" onClick={goBackToMerchant} data-testid="pay-back" style={{ background: "none", border: 0 }}>Return to merchant</button>}
          </section>
        )}
      </main>
    </div>
  );
}

// ---- SuryaPe checkout pieces (same markup as backend/services/checkout_page.py) ----
function BrandEdge() {
  return <div className="brand-edge"><img src="/pay/assets/supercash.svg" alt="SuperCash" width="148" height="53" decoding="async" /></div>;
}
function AppIcons() {
  return (
    <span className="app-icons">
      <img className="app-icon" src="/pay/assets/google-pay.png" width="28" height="28" alt="Google Pay" decoding="async" />
      <img className="app-icon" src="/pay/assets/phonepe.png" width="28" height="28" alt="PhonePe" decoding="async" />
      <img className="app-icon" src="/pay/assets/paytm.png" width="28" height="28" alt="Paytm" decoding="async" />
    </span>
  );
}
function Arrow() {
  return <svg className="button-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14m-5-5 5 5-5 5" /></svg>;
}
function Chevron() {
  return <svg className="disclosure-arrow" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>;
}
function QrGlyph() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 3h6v6H3zM15 3h6v6h-6zM3 15h6v6H3zM15 15v3h3v3M21 15v3M12 3v3M3 12h3M12 12h3v3M12 21v-3M18 12h3" /></svg>;
}
function ResultIcons() {
  return (
    <div className="result-icon" aria-hidden="true">
      <svg className="result-symbol result-symbol--success" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path pathLength="1" d="m5 12 4 4L19 6" /></svg>
      <svg className="result-symbol result-symbol--error" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path pathLength="1" d="m7 7 10 10M17 7 7 17" /></svg>
      <svg className="result-symbol result-symbol--pending" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><circle cx="12" cy="12" r="8" /><path d="M12 7v5l3 2" /></svg>
    </div>
  );
}
